// Runtime data for the page being shown. THIS FILE CONTAINS NO CONTENT.
//
// Content lives in two places:
//   src/data/shared/*            copy and assets shared by every proposal
//   src/data/proposals/<code>.js one client's numbers, comps, photos and dates
//
// Components import `siteData` from here. It is assembled when a proposal is requested:
// shared content + that one client's file. Only the requested proposal's module is ever loaded,
// so one client's page never contains another's data.
//
// `siteData` is a live binding: App sets it with setSiteData() before any section renders.
import { agent, testimonials } from "@/data/shared/agent";
import { videoReviews } from "@/data/shared/videoReviews";
import { marketingPlan } from "@/data/shared/marketingPlan";
import { homePrep, prepNote } from "@/data/shared/homePrep";
import { launch } from "@/data/shared/launch";
import { showingLadder, showingLadderCaption, pricingStrategies } from "@/data/shared/pricingStrategy";
import { process as listingProcess } from "@/data/shared/process";
import { timeline } from "@/data/shared/timeline";
import { communication, promise } from "@/data/shared/promise";
import { offers } from "@/data/shared/offers";
import { marketingTiles } from "@/data/shared/marketingTiles";
import { listingsByAdam } from "@/data/shared/listingsByAdam";
import { assets, sampleHeroPhoto, disclaimers } from "@/data/shared/brand";
import { copy } from "@/data/shared/copy";

export let siteData = null;
export function setSiteData(next) {
  siteData = next;
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
export function deepMerge(base, over) {
  if (!isObj(base) || !isObj(over)) return over === undefined ? base : over;
  const out = { ...base };
  for (const k of Object.keys(over)) out[k] = deepMerge(base[k], over[k]);
  return out;
}
const fillStr = (tpl, vars) => String(tpl).replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined && vars[k] !== null ? vars[k] : ""));

// Hero photo: the client's own photo (meta.heroMode "photo") or the branded sample ("sample").
export function heroPhotoFor(meta, subject) {
  if (!meta) return null;
  if (meta.heroMode === "sample") return sampleHeroPhoto;
  if (meta.heroMode === "photo" && meta.heroImage) {
    if (typeof meta.heroImage === "string") {
      return { sources: [{ src: meta.heroImage, width: 1600 }], alt: `Front of ${subject?.address || "the home"}`, label: null };
    }
    return { label: null, ...meta.heroImage };
  }
  return null;
}

// Which sections this proposal can show. A section is listed only when its data is present.
export function sectionsFor(p) {
  if (!p) return [];
  const t = p.sectionToggles || {};
  const on = (key) => t[key] !== false;
  const list = ["your-home"];
  if (on("nearbySales") && Array.isArray(p.nearbySales) && p.nearbySales.length) list.push("nearby");
  if (p.market && p.seasonal) list.push("market");
  if (Array.isArray(p.comps) && p.comps.length) list.push("pricing");
  if (Array.isArray(p.pricingStories) && p.pricingStories.length) list.push("pricing-strategy");
  list.push("launch", "getting-ready", "marketing", "communication", "marketing-plan", "offers");
  if (on("proceeds") && p.proceeds && p.proceeds.show !== false) list.push("proceeds");
  list.push("why-adam", "easy-exit", "timeline", "contact");
  return list;
}

const SHARED = {
  agent,
  testimonials,
  videoReviews,
  marketingPlan,
  homePrep,
  prepNote,
  launch,
  showingLadder,
  showingLadderCaption,
  pricingStrategies,
  process: listingProcess,
  timeline,
  communication,
  promise,
  offers,
  marketing: marketingTiles,
  recentListings: listingsByAdam,
  assets,
  disclaimers,
  ui: copy,
};

/**
 * Build the page data for one proposal (or the shared-only data for status pages when `p` is null).
 * The result has the same shape every section component expects.
 */
export function assembleSiteData(p) {
  const overrides = (p && p.overrides) || {};
  // Any shared key can be overridden per proposal: objects deep-merge, arrays and strings replace.
  const shared = { ...SHARED };
  for (const k of Object.keys(overrides)) shared[k] = deepMerge(SHARED[k], overrides[k]);
  const ui = shared.ui;

  if (!p) {
    return { ...shared, meta: { isDraft: false, status: "none", pageTitle: ui.pageTitle }, sections: [], toggles: {} };
  }

  const sections = sectionsFor(p);
  const toggles = p.sectionToggles || {};
  const preparedFor = isObj(p.meta.preparedFor) ? p.meta.preparedFor : { value: p.meta.preparedFor, verify: false };
  const meta = {
    ...p.meta,
    preparedFor,
    // Footer line. A proposal can set meta.privateNote to replace the shared template.
    privateNote: p.meta.privateNote || fillStr(ui.footer.privateNote, { name: preparedFor.value }),
    isDraft: p.meta.status === "draft",
    pageTitle: ui.pageTitle,
  };
  const subject = { ...p.subject, heroPhoto: heroPhotoFor(p.meta, p.subject) };

  const uiOut = {
    ...ui,
    nav: { ...ui.nav, links: ui.nav.links.filter((l) => sections.includes(l.id)) },
    sectionNav: Object.fromEntries(
      Object.entries(ui.sectionNav).map(([id, target]) => [id, target && ui.nav.links.some((l) => l.id === target) && sections.includes(target) ? target : null]),
    ),
    nearby: { ...ui.nearby, headline: fillStr(ui.nearby.headline, { area: p.nearbySalesTitle || "" }).replace(/\s*\(\)\s*$/, ""), map: { ...ui.nearby.map, mid: toggles.nearbyMap === false ? null : p.nearbyMapMid || null, color: "231F20" } },
    fits: { ...ui.fits, map: { ...ui.fits.map, mid: toggles.compsMap === false ? null : p.compsMapMid || null, color: "231F20" } },
  };

  return {
    ...shared,
    ui: uiOut,
    meta,
    subject,
    pricing: p.pricing,
    market: p.market,
    seasonal: p.seasonal,
    comps: p.comps || [],
    compsSummaryCheck: p.compsSummaryCheck,
    compsAttribution: p.compsAttribution || "",
    nearbySales: p.nearbySales || [],
    pricingStories: p.pricingStories || [],
    proceeds: p.proceeds,
    sections,
    toggles,
  };
}
